export interface Photo {
    url: string;
    type: 'image' | 'video';
    caption?: string;
}

// Database schema types (merged from @ys520/shared)
export interface Event {
    id: number;
    name: string;
    month: string;
    seasonId: string;
    createdAt: Date;
    updatedAt: Date;
    photos?: DatabasePhoto[];
}

export interface Season {
    id: string;
    name: string;
    year: number;
    createdAt: Date;
    updatedAt: Date;
    eventCount?: number;
}

// Mirrors the PhotoAudience enum in the Prisma schema by hand; src never
// imports @prisma/client, and the backend imports this list rather than
// restating it.
export const PHOTO_AUDIENCES = ['PERSONAL', 'FAMILY'] as const;
export type PhotoAudience = (typeof PHOTO_AUDIENCES)[number];

// Wire vocabulary for how much of the library a viewer may see. Lowercase and
// distinct from PhotoAudience: 'personal' shows every photo, 'family' only the
// FAMILY-tagged subset. Backend and frontend both import this list.
export const VIEWER_MODES = ['personal', 'family'] as const;
export type ViewerMode = (typeof VIEWER_MODES)[number];

const isVocabularyMember = <Member extends string>(
  vocabulary: readonly Member[],
  value: unknown
): value is Member => typeof value === 'string' && (vocabulary as readonly string[]).includes(value);

export const isPhotoAudience = (value: unknown): value is PhotoAudience =>
  isVocabularyMember(PHOTO_AUDIENCES, value);

export const isViewerMode = (value: unknown): value is ViewerMode =>
  isVocabularyMember(VIEWER_MODES, value);

export interface DatabasePhoto {
    id: number;
    eventId: number;
    mediaType: 'image' | 'video';
    caption: string | null;
    displayOrder: number;
    audience: PhotoAudience;
}

// Grouped photos structure for frontend (computed by PhotoService)
export interface PhotosByEvent {
    [eventName: string]: {
        event: Event & { year: number }; // Include year from season relationship for compatibility
        photos: DatabasePhoto[];
        season: string; // seasonId from database
        avatarPhotoId: number | null;
    };
}


export type { SeasonContext } from './adminTypes';
